import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useUser } from '../contexts/UserContext';
import { rotateVideos, dailySeed } from '../lib/videoRotation';

const relativeTime = (isoString) => {
  const diff = Date.now() - new Date(isoString).getTime();
  const hours = Math.floor(diff / 3_600_000);
  const days = Math.floor(hours / 24);
  if (days >= 7) {
    // Short date instead of a decaying "Nw" counter
    const d = new Date(isoString);
    const opts = { month: 'short', day: 'numeric' };
    if (d.getFullYear() !== new Date().getFullYear()) opts.year = 'numeric';
    return d.toLocaleDateString('en-US', opts);
  }
  if (days > 0) return `${days}d`;
  if (hours > 0) return `${hours}h`;
  return 'now';
};

const normalizeSubmission = (sub) => ({
  id: sub.id,                          // UUID string
  title: sub.title,
  youtubeId: sub.youtube_id,
  // 043: a submission is now either a YouTube link or a member's own file on
  // Cloudflare Stream. The same field names the curated path uses, so
  // hlsPlayer's mountVideo picks the source with no extra branch:
  // youtubeId -> iframe, else streamHlsUrl -> HLS.
  //
  // get_approved_videos only returns an upload row once Cloudflare reports
  // ready, so streamHlsUrl is never null on an upload that reaches here.
  streamHlsUrl: sub.stream_hls_url || null,
  storageUrl: null,
  thumbnail: sub.thumbnail_url,
  category: sub.category,
  postTitle: sub.post_title || null,
  commentary: sub.commentary || null,
  creator: sub.submitter_name || 'DSP Community',
  submitterId: sub.submitter_id || null,
  // Set when the video was suggested from a table post (039).
  sourcePost: sub.source_post_id && sub.source_table_slug
    ? { id: sub.source_post_id, tableSlug: sub.source_table_slug, tableName: sub.source_table_name || null }
    : null,
  views: null,
  likes: null,
  duration: formatDuration(sub.duration_seconds),
  // Same reasoning as visibleSince: a video approved a month after it was
  // submitted should read "now" on the day it appears, not "30d".
  timestamp: relativeTime(sub.reviewed_at || sub.submitted_at),
  submittedAt: sub.submitted_at,
  // ⚠️ 046: "new" means when it became VISIBLE, not when it was submitted.
  // A YouTube link is approved in minutes so the two nearly coincide; an upload
  // sits in the queue while a moderator WATCHES it. Measuring freshness from
  // submitted_at burned the 7-day feed pin during review, so a video approved
  // on day 8 went live already stale — never pinned, and sorted below older
  // videos in "Latest". reviewed_at is the moment it appeared.
  // Falls back for rows approved before 046 returned the column.
  visibleSince: sub.reviewed_at || sub.submitted_at,
  publishedAt: sub.reviewed_at || sub.submitted_at,   // cross-source sorting
  isSubmission: true,
  isCurated: false,
});

// mm:ss / h:mm:ss. Cloudflare returns duration on every Stream video, so the
// duration badge in ExploreScreen/FeedScreen — dead for curated videos until
// now, since this always hardcoded null — starts working once a row is migrated.
const formatDuration = (secs) => {
  if (typeof secs !== 'number' || !isFinite(secs) || secs <= 0) return null;
  const s = Math.round(secs);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`
    : `${m}:${String(r).padStart(2, '0')}`;
};

const normalizeCurated = (v) => ({
  id: v.id,
  title: v.public_title,
  creator: v.author,
  category: v.category,
  // storageUrl is kept alongside streamHlsUrl for the dual-read period: a
  // migrated row plays from Stream, an un-migrated one still plays from
  // Supabase, and reverting the client is a complete rollback either way.
  storageUrl: v.storage_url,
  streamHlsUrl: v.stream_hls_url || null,
  streamUid: v.stream_uid || null,
  streamStatus: v.stream_status || 'legacy',
  thumbnail: v.thumbnail_url,
  hasCaptions: v.has_captions,
  captionUrl: v.caption_url || null,
  commentary: v.description || null,
  tags: v.tags || [],
  youtubeId: null,
  views: null,
  likes: null,
  duration: formatDuration(v.duration_seconds),
  timestamp: v.date_uploaded
    ? new Date(v.date_uploaded).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    : null,
  // Raw date alongside the formatted one. `timestamp` is display text ("Sep
  // 2026") and cannot be sorted; anything wanting newest-first needs this.
  publishedAt: v.date_uploaded || null,
  isCurated: true,
  isSubmission: false,
});

export function useVideos() {
  const { session } = useUser();
  const [submissions, setSubmissions] = useState([]);
  const [curated, setCurated] = useState([]);
  const [loading, setLoading] = useState(true);

  // Seed captured once per mount — no mid-session reshuffle at midnight
  const userId = session?.user?.id;
  const seed = useMemo(() => dailySeed(userId), [userId]);

  const fetchAll = useCallback(async () => {
    const [approvedResult, curatedResult] = await Promise.all([
      supabase.rpc('get_approved_videos'),
      supabase.rpc('get_curated_videos'),
    ]);
    if (approvedResult.error) console.error('get_approved_videos:', approvedResult.error);
    if (curatedResult.error) console.error('get_curated_videos:', curatedResult.error);
    setSubmissions((approvedResult.data ?? []).map(normalizeSubmission));
    setCurated((curatedResult.data ?? []).map(normalizeCurated));
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchAll().finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [fetchAll]);

  // Daily weighted rotation: curated (staff picks) usually float up,
  // fresh submissions get a lift, order changes per user per day.
  // Real sources only: the mock videos (real YouTube videos credited to
  // invented creators) were dropped for the App Store submission.
  const videos = useMemo(
    () => rotateVideos([...curated, ...submissions], seed),
    [curated, submissions, seed]
  );

  return { videos, loading, refresh: fetchAll };
}
